import { useRef, useState } from 'react'

import { UploadIcon } from '@heroicons/react/outline'
import { useThrowingFn } from 'beskar/landing'

import classNames from 'classnames'
import { ComponentPropsWithRef, forwardRef } from 'react'
import {
    Button,
    Checkbox,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Radio,
    cn,
} from '@nextui-org/react'
import { Control, useController } from 'react-hook-form'
import { ColorResult, SketchPicker } from 'react-color'

export const Input = forwardRef<any, ComponentPropsWithRef<'input'>>(
    function Textarea({ className, ...rest }, ref) {
        return (
            <input
                ref={ref}
                className={classNames(
                    'w-full tracking-wide text-gray-900 ',
                    'rounded-lg px-2.5 py-[6px] border-gray-300 focus:ring-2 block',
                    'bg-gray-50 focus:ring-gray-400/70 ring-1 ring-gray-200 dark:ring-gray-800 focus:border-gray-500',
                    'dark:text-white dark:placeholder-gray-400',
                    'dark:focus:ring-gray-500 ',
                    ' dark:bg-gray-700',
                    className,
                )}
                {...rest}
            />
        )
    },
)

export function GhostButton({
    className,
    ...rest
}: ComponentPropsWithRef<'button'>) {
    return (
        <button
            className={classNames(
                'p-2 py-[6px] ',
                'max-w-max justify-center font-medium items-center shrink-0 min-h-[34px]',
                'flex gap-2 ease-in-out appearance-none active:opacity-100',
                'duration-300 whitespace-nowrap hover:bg-gray-100 dark:hover:bg-gray-800 transition-all rounded',
                className,
            )}
            type='button'
            {...rest}
        />
    )
}

export function Spinner({ className = '' }) {
    return (
        <>
            <style jsx>{`
                .spinner {
                    position: relative;
                    pointer-events: none;
                }

                .spinner::after {
                    content: '';
                    position: absolute !important;
                    top: calc(50% - (1em / 2));
                    left: calc(50% - (1em / 2));
                    display: block;
                    width: 1em;
                    height: 1em;
                    border: 2px solid currentColor;
                    border-radius: 9999px;
                    border-right-color: transparent;
                    border-top-color: transparent;
                    animation: spinAround 500ms infinite linear;
                }

                @keyframes spinAround {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }
            `}</style>

            <div className={classNames('spinner w-5 h-5', className)} />
        </>
    )
}

export const RadioCard = (props) => {
    const { children, ...otherProps } = props

    return (
        <Radio
            {...otherProps}
            classNames={{
                base: cn(
                    'flex m-0 bg-content2 min-w-full hover:bg-content2 items-center justify-between',
                    'flex-row-reverse cursor-pointer rounded-lg gap-4 p-4 border-2 border-transparent',
                    'data-[selected=true]:border-primary',
                ),
            }}
        >
            {children}
        </Radio>
    )
}

export const CheckboxCard = (props) => {
    const { children, description, ...otherProps } = props

    return (
        <Checkbox
            {...otherProps}
            classNames={{
                base: cn(
                    'flex m-0 bg-content2 min-w-full hover:bg-content2 items-center justify-between',
                    'flex-row-reverse cursor-pointer rounded-lg gap-4 p-4 border-2 border-transparent',
                    'data-[selected=true]:border-primary',
                ),
            }}
        >
            <div className=''>
                <div className=''>{children}</div>
                <div className='text-sm opacity-60'>{description}</div>
            </div>
        </Checkbox>
    )
}

export function ColorPicker({ onChange, value, children, defaultValue }) {
    function colorString(c: ColorResult) {
        return `rgba(${c.rgb.r},${c.rgb.g},${c.rgb.b},${c.rgb.a || 1})`
    }

    return (
        <Popover>
            <PopoverTrigger>
                <Button className='flex gap-2'>
                    {children}
                    <div
                        style={{ background: value }}
                        className='w-4 h-4 shadow rounded '
                    ></div>
                </Button>
            </PopoverTrigger>

            <PopoverContent className='select-none !bg-transparent !border-0 min-w-0 max-w-max'>
                <SketchPicker
                    className=''
                    onChange={(c) => {
                        onChange(colorString(c))
                    }}
                    onChangeComplete={(c) => {
                        onChange(colorString(c))
                    }}
                    color={value || defaultValue}
                />
            </PopoverContent>
        </Popover>
    )
}
